import { produce } from 'immer'
import _ from 'lodash'
import { create } from 'zustand'

import type { DashboardChartResponse, UnionChart } from 'features/charts/types'
import type { DashboardControlsConfig } from 'features/dashboard/types'
import { ExportData, TableChartData } from 'types/project'

export const defaultDashboardControls: Record<string, unknown> = {
  chartId: null,
  tier1: null,
  tier2: null,
  tier3: null,
  legend: true,
  fullAxis: true,
  hasLabels: true,
  hasOverlay: false,
  treeView: true,
  origin: true,
  labels: true,
  trendlines: false,
  shadedZones: true,
  sortedByCategory: false,
  isAlignScales: true,
  isAlignOrder: true,
  multiChart: false,
  fontSize: 'medium',
  timeInterval: null,
  reviewsCount: 0,
  postType: 'post',
  interval: null,
  isExpandChart: false,
  tableSort: [],
  order: null,
  isDelta: false,
  pageSize: undefined,
  showPercentChange: true,
  wrapLabels: false,
  maxAxesLength: 30,
  isUnlinked: false,
  matrixScaleVolume: false,
  matrixSort: false,
  matrixSortAxis: undefined,
  matrixSortValue: undefined,
  matrixSpectrumColors: false,
  matrixFlipAxis: false,
  expandedCategories: [],
  hideParentCategories: false,
  hideXAxis: false,
  hideYAxis: false,
  showCategories: undefined,
  disabledLegendItems: [],
}

// ---------------------------------------------------------------------------
// State type
// ---------------------------------------------------------------------------

export type ChartStoreState = {
  globalControls: Record<string, unknown>

  dashboardControls: DashboardControlsConfig[]
  setDashboardControls: (values: DashboardControlsConfig[]) => void
  updateDashboardControls: (
    comparativeIndex: number,
    field: string,
    value: string | number | boolean | string[] | null,
  ) => void
  updateGlobalDashboardControl: (
    field: string,
    value: string | number | boolean | null,
  ) => void

  chartData: DashboardChartResponse[]
  setChartData: (value: DashboardChartResponse[]) => void
  updateChartData: (
    comparativeIndex: number,
    value: DashboardChartResponse,
  ) => void

  currentChartList: UnionChart[]
  setCurrentChartList: (value: UnionChart[]) => void
  updateCurrentChartList: (
    comparativeIndex: number,
    value: UnionChart | undefined,
  ) => void

  orderedTableData: TableChartData[]
  setOrderedTableData: (value: TableChartData[]) => void

  isExportDrawerOpen: boolean
  setIsExportDrawerOpen: (open: boolean) => void
  exportData: ExportData | undefined
  setExportData: (data: ExportData | undefined) => void
  isExporting: boolean
  setIsExporting: (exporting: boolean) => void

  resetChartStore: () => void
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialChartStoreState = {
  globalControls: {} as Record<string, unknown>,
  dashboardControls: [defaultDashboardControls] as DashboardControlsConfig[],
  chartData: [] as DashboardChartResponse[],
  currentChartList: [] as UnionChart[],
  orderedTableData: [] as TableChartData[],
  isExportDrawerOpen: false,
  exportData: undefined as ExportData | undefined,
  isExporting: false,
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChartStore = create<ChartStoreState>()((set) => ({
  ...initialChartStoreState,

  setDashboardControls: (values) =>
    set(
      produce((state: ChartStoreState) => {
        state.dashboardControls = values
      }),
    ),

  updateDashboardControls: (comparativeIndex, field, value) =>
    set(
      produce((state: ChartStoreState) => {
        ;(
          state.dashboardControls[comparativeIndex] as unknown as Record<
            string,
            unknown
          >
        )[_.camelCase(field)] = value
      }),
    ),

  updateGlobalDashboardControl: (field, value) =>
    set(
      produce((state: ChartStoreState) => {
        state.dashboardControls.forEach((_control, index) => {
          ;(
            state.dashboardControls[index] as unknown as Record<string, unknown>
          )[_.camelCase(field)] = value
        })
      }),
    ),

  setChartData: (value) =>
    set(
      produce((state: ChartStoreState) => {
        state.chartData = value
      }),
    ),

  updateChartData: (comparativeIndex, value) =>
    set(
      produce((state: ChartStoreState) => {
        state.chartData[comparativeIndex] = value
      }),
    ),

  setCurrentChartList: (value) =>
    set(
      produce((state: ChartStoreState) => {
        state.currentChartList = value
      }),
    ),

  updateCurrentChartList: (comparativeIndex, value) =>
    set(
      produce((state: ChartStoreState) => {
        state.currentChartList[comparativeIndex] = value as UnionChart
      }),
    ),

  setOrderedTableData: (value) =>
    set(
      produce((state: ChartStoreState) => {
        state.orderedTableData = value
      }),
    ),

  setIsExportDrawerOpen: (open) =>
    set(
      produce((state: ChartStoreState) => {
        state.isExportDrawerOpen = open
      }),
    ),

  setExportData: (data) =>
    set(
      produce((state: ChartStoreState) => {
        state.exportData = data
      }),
    ),

  setIsExporting: (exporting) =>
    set(
      produce((state: ChartStoreState) => {
        state.isExporting = exporting
      }),
    ),

  resetChartStore: () => set(initialChartStoreState),
}))
